import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HelpCircle } from 'lucide-react';
import { FAQS, LOGO, whatsappLink, DEFAULT_MESSAGE } from '@/data/site';
import { RevealWords } from './fx/RevealWords';
import { Magnetic } from './fx/Magnetic';
import { WhatsAppIcon, Paw } from './brand/Shapes';
import { Wave } from './fx/Wave';

const Typing = () => (
  <span className="inline-flex gap-1 px-1 py-1" aria-label="digitando">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-2 h-2 rounded-full bg-pet-muted/60"
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.12 }}
      />
    ))}
  </span>
);

/** Pergunta (balão do tutor) + resposta da Cãopanhia, com "digitando…" antes. */
const Item = ({ item, index, open, onToggle }) => {
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setTyping(true);
    const t = setTimeout(() => setTyping(false), 650);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <li className="space-y-2">
      <div className="flex justify-end items-end gap-2">
        <motion.button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`faq-${index}`}
          whileHover={{ scale: 1.03, rotate: -1 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 500, damping: 14 }}
          className={`max-w-[88%] text-left rounded-[22px] rounded-br-md px-4 md:px-5 py-3 font-extrabold text-[16px] md:text-lg shadow-sm transition-colors ${
            open ? 'bg-pet-blue text-white' : 'bg-pet-blue-soft text-pet-blue-deep hover:bg-pet-sky'
          }`}
        >
          {item.q}
        </motion.button>
        <span className="w-8 h-8 rounded-full bg-pet-pink text-white flex items-center justify-center shrink-0 mb-1 ring-2 ring-white" aria-hidden="true">
          <Paw className="w-4 h-4" />
        </span>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`faq-${index}`}
            className="flex items-end gap-2"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
          >
            <img src={LOGO} alt="" className="w-8 h-8 rounded-full ring-2 ring-white shrink-0 mb-1" />
            <motion.div
              className="max-w-[85%] rounded-[22px] rounded-bl-md bg-white px-4 md:px-5 py-3 shadow-sm text-[16px] md:text-lg font-semibold leading-relaxed text-pet-ink"
              initial={{ scale: 0.6, originX: 0, originY: 1 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 18 }}
            >
              {typing ? <Typing /> : item.a}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
};

export const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-nav="faq" className="relative bg-pet-cream py-20 md:py-28">
      <Wave color="#FDFBF7" className="absolute bottom-[calc(100%-1px)] inset-x-0" />
      <div className="container-cp grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-[14vh]">
          <p className="kicker kicker-ears bg-pet-mint text-pet-mint-ink mb-6 rotate-2">
            <HelpCircle className="w-4 h-4" /> Dúvidas frequentes
          </p>
          <RevealWords
            className="font-display font-medium text-[clamp(2.4rem,4.4vw,3.9rem)] leading-[1.05] text-pet-ink"
            parts={[{ text: 'Tudo que você' }, { text: 'precisa saber', className: 'text-pet-blue sticker-text' }]}
          />
          <p className="speech mt-7 inline-block rounded-[24px] bg-white px-5 py-3.5 text-[17px] leading-relaxed font-bold text-pet-ink/80 max-w-sm shadow-[0_12px_30px_-18px_rgba(45,55,72,0.4)]">
            Respostas rápidas para suas principais dúvidas. Ainda ficou com alguma? Fale com a gente!
          </p>
          <Magnetic className="mt-8">
            <a href={whatsappLink(DEFAULT_MESSAGE)} target="_blank" rel="noopener noreferrer" className="btn-pink pl-2.5">
              <span className="w-9 h-9 rounded-full bg-pet-whats text-white flex items-center justify-center">
                <WhatsAppIcon className="w-5 h-5" />
              </span>
              Perguntar no WhatsApp
            </a>
          </Magnetic>
        </div>

        {/* "celular" com a conversa */}
        <div className="lg:col-span-7">
          <div className="rounded-[40px] bg-[#EAF4FF] ring-[7px] ring-white shadow-[0_30px_70px_-40px_rgba(77,150,255,0.8)] overflow-hidden">
            <div className="flex items-center gap-3 bg-white px-5 py-4">
              <img src={LOGO} alt="" className="w-11 h-11 rounded-full ring-2 ring-pet-pink" />
              <div className="leading-tight">
                <p className="font-display text-lg text-pet-ink">Cãopanhia Pet</p>
                <p className="text-xs font-extrabold text-pet-mint-ink flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-pet-mint-deep animate-pulse" /> toque numa pergunta 👇
                </p>
              </div>
            </div>
            <ul className="p-4 md:p-6 space-y-4 min-h-[420px]">
              {FAQS.map((item, i) => (
                <Item key={item.q} item={item} index={i} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
